/**
 * parseExcelStyled — Rich Excel Parser with Full Styling Extraction
 *
 * Parses an Excel file and extracts:
 * - Cell values (text, numbers, dates, formulas, hyperlinks)
 * - Cell styles (fill colors, font, bold, italic, alignment, borders)
 * - Merged cell ranges
 * - Row heights and column widths
 * - Sheet-level metadata (frozen panes, etc.)
 *
 * The output is a structured JSON format that can be rendered as a
 * near-faithful reproduction of the original spreadsheet in HTML/CSS.
 */

import * as ExcelJS from 'exceljs';

// Types

export interface CellStyle {
	/** Background fill color (hex, e.g. "#4F6228") */
	fill?: string;
	/** Fill pattern type */
	fillPattern?: string;
	/** Font color (hex) */
	color?: string;
	/** Font bold */
	bold?: boolean;
	/** Font italic */
	italic?: boolean;
	/** Font underline */
	underline?: boolean;
	/** Font strikethrough */
	strike?: boolean;
	/** Font size in pt */
	fontSize?: number;
	/** Font family */
	fontFamily?: string;
	/** Horizontal alignment */
	align?: 'left' | 'center' | 'right' | 'fill' | 'justify';
	/** Vertical alignment */
	verticalAlign?: 'top' | 'middle' | 'bottom';
	/** Text wrap */
	wrapText?: boolean;
	/** Text indent */
	indent?: number;
	/** Text rotation (degrees) */
	rotation?: number;
	/** Border styles */
	borders?: {
		top?: BorderStyle;
		bottom?: BorderStyle;
		left?: BorderStyle;
		right?: BorderStyle;
	};
	/** Number format string (e.g. "#,##0.00", "yyyy-mm-dd") */
	numberFormat?: string;
}

export interface BorderStyle {
	style: string; // 'thin', 'medium', 'thick', 'dashed', 'dotted', 'double'
	color?: string;
}

export interface StyledCell {
	/** The cell value (string, number, boolean, or null) */
	value: string | number | boolean | null;
	/** Cell styling information */
	style?: CellStyle;
	/** If this cell is part of a merge, its merge range (e.g. "A1:C2") */
	mergeRange?: string;
	/** Whether this cell is the master (top-left) of a merged range */
	isMergeMaster?: boolean;
	/** If merged, how many columns this cell spans */
	colSpan?: number;
	/** If merged, how many rows this cell spans */
	rowSpan?: number;
	/** Column letter (A, B, C, ...) */
	col: string;
	/** Original formula if present */
	formula?: string;
	/** Hyperlink URL if present */
	hyperlink?: string;
}

export interface StyledRow {
	/** 0-based row index */
	rowIndex: number;
	/** Row height in pixels (default ~20) */
	height?: number;
	/** Whether the row is hidden */
	hidden?: boolean;
	/** Outline level (grouping) */
	outlineLevel?: number;
	/** Cells keyed by column letter (A, B, C...) */
	cells: Record<string, StyledCell>;
}

export interface SheetMetadata {
	/** Merged cell ranges: ["A1:C2", "D5:D8", ...] */
	mergedCells: string[];
	/** Column widths keyed by letter: { A: 120, B: 80, ... } in pixels */
	columnWidths: Record<string, number>;
	/** Column hidden states */
	hiddenColumns: string[];
	/** Total number of columns used */
	totalColumns: number;
	/** Total number of rows */
	totalRows: number;
	/** Ordered array of column letters used */
	columnOrder: string[];
	/** Frozen pane row (0 = none) */
	frozenRow?: number;
	/** Frozen pane column (0 = none) */
	frozenCol?: number;
	/** Default row height */
	defaultRowHeight?: number;
	/** Default column width in pixels */
	defaultColWidth?: number;
	/** Right-to-left sheet */
	rightToLeft?: boolean;
}

export interface StyledSheetInfo {
	name: string;
	rowCount: number;
}

export interface ParseExcelStyledResult {
	/** Column letters in order (A, B, C, ...) */
	headers: string[];
	/** Display names for each column (from header row, if detected) */
	headerLabels: Record<string, string>;
	/** All rows with styled cells */
	rows: StyledRow[];
	/** Worksheet-level metadata */
	metadata: SheetMetadata;
	/** Available sheets */
	sheets: StyledSheetInfo[];
	/** Currently selected sheet name */
	selectedSheet: string;
}

// Color Extraction Helpers

/**
 * Convert an ExcelJS color object to a CSS hex string.
 * ExcelJS colors can be: { argb: "FF4F6228" }, { theme: 0, tint: -0.25 }, { indexed: 64 }
 */
function extractColor(color: Partial<ExcelJS.Color> | undefined): string | undefined {
	if (!color) return undefined;

	// ARGB format: "FFRRGGBB" or "RRGGBB"
	if (color.argb) {
		const argb = color.argb;
		// Strip alpha channel if present (first 2 chars)
		if (argb.length === 8) {
			return `#${argb.substring(2)}`;
		}
		return `#${argb}`;
	}

	// Theme colors — map common theme indices to reasonable defaults
	if (color.theme !== undefined) {
		const themeColors: Record<number, string> = {
			0: '#FFFFFF', // Background 1 (usually white)
			1: '#000000', // Text 1 (usually black)
			2: '#E7E6E6', // Background 2
			3: '#44546A', // Text 2
			4: '#4472C4', // Accent 1
			5: '#ED7D31', // Accent 2
			6: '#A5A5A5', // Accent 3
			7: '#FFC000', // Accent 4
			8: '#5B9BD5', // Accent 5
			9: '#70AD47', // Accent 6
		};
		let base = themeColors[color.theme] || '#000000';

		// Apply tint if present
		const tint = (color as { tint?: number }).tint;
		if (tint !== undefined && tint !== 0) {
			base = applyTint(base, tint);
		}
		return base;
	}

	// Indexed colors — Excel's 64-color palette
	const indexed = (color as { indexed?: number }).indexed;
	if (indexed !== undefined) {
		return getIndexedColor(indexed);
	}

	return undefined;
}

/**
 * Apply a tint to a hex color.
 * Positive tint = lighter (toward white), negative = darker (toward black).
 */
function applyTint(hex: string, tint: number): string {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);

	const apply = (c: number) => {
		if (tint < 0) {
			return Math.round(c * (1 + tint));
		} else {
			return Math.round(c + (255 - c) * tint);
		}
	};

	const nr = Math.min(255, Math.max(0, apply(r)));
	const ng = Math.min(255, Math.max(0, apply(g)));
	const nb = Math.min(255, Math.max(0, apply(b)));

	return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}`;
}

/** Excel indexed color palette (standard 64 colors + common extensions) */
function getIndexedColor(index: number): string | undefined {
	const palette: Record<number, string> = {
		0: '#000000',
		1: '#FFFFFF',
		2: '#FF0000',
		3: '#00FF00',
		4: '#0000FF',
		5: '#FFFF00',
		6: '#FF00FF',
		7: '#00FFFF',
		8: '#000000',
		9: '#FFFFFF',
		10: '#FF0000',
		11: '#00FF00',
		12: '#0000FF',
		13: '#FFFF00',
		14: '#FF00FF',
		15: '#00FFFF',
		16: '#800000',
		17: '#008000',
		18: '#000080',
		19: '#808000',
		20: '#800080',
		21: '#008080',
		22: '#C0C0C0',
		23: '#808080',
		24: '#9999FF',
		25: '#993366',
		26: '#FFFFCC',
		27: '#CCFFFF',
		28: '#660066',
		29: '#FF8080',
		30: '#0066CC',
		31: '#CCCCFF',
		32: '#000080',
		33: '#FF00FF',
		34: '#FFFF00',
		35: '#00FFFF',
		36: '#800080',
		37: '#800000',
		38: '#008080',
		39: '#0000FF',
		40: '#00CCFF',
		41: '#CCFFFF',
		42: '#CCFFCC',
		43: '#FFFF99',
		44: '#99CCFF',
		45: '#FF99CC',
		46: '#CC99FF',
		47: '#FFCC99',
		48: '#3366FF',
		49: '#33CCCC',
		50: '#99CC00',
		51: '#FFCC00',
		52: '#FF9900',
		53: '#FF6600',
		54: '#666699',
		55: '#969696',
		56: '#003366',
		57: '#339966',
		58: '#003300',
		59: '#333300',
		60: '#993300',
		61: '#993366',
		62: '#333399',
		63: '#333333',
		64: '#000000', // System foreground (auto)
	};
	return palette[index];
}

// Column Letter Helpers

/** Convert 1-based column number to letter(s): 1→A, 26→Z, 27→AA */
function colNumberToLetter(col: number): string {
	let result = '';
	let c = col;
	while (c > 0) {
		c--;
		result = String.fromCharCode(65 + (c % 26)) + result;
		c = Math.floor(c / 26);
	}
	return result;
}

// Style Extraction

/** Extract CellStyle from an ExcelJS cell */
function extractCellStyle(cell: ExcelJS.Cell): CellStyle | undefined {
	const style: CellStyle = {};
	let hasStyle = false;

	// Fill
	const fill = cell.fill;
	if (fill) {
		if (fill.type === 'pattern' && fill.pattern !== 'none') {
			const fg = extractColor((fill as ExcelJS.FillPattern).fgColor);
			const bg = extractColor((fill as ExcelJS.FillPattern).bgColor);
			if (fg) {
				style.fill = fg;
				style.fillPattern = (fill as ExcelJS.FillPattern).pattern;
				hasStyle = true;
			} else if (bg) {
				style.fill = bg;
				hasStyle = true;
			}
		} else if (fill.type === 'gradient') {
			// For gradients, use the first stop color
			const stops = (fill as { stops?: { color: ExcelJS.Color }[] }).stops;
			if (stops && stops.length > 0) {
				const c = extractColor(stops[0].color);
				if (c) {
					style.fill = c;
					hasStyle = true;
				}
			}
		}
	}

	// Font
	const font = cell.font;
	if (font) {
		if (font.bold) {
			style.bold = true;
			hasStyle = true;
		}
		if (font.italic) {
			style.italic = true;
			hasStyle = true;
		}
		if (font.underline) {
			style.underline = true;
			hasStyle = true;
		}
		if (font.strike) {
			style.strike = true;
			hasStyle = true;
		}
		if (font.size) {
			style.fontSize = font.size;
			hasStyle = true;
		}
		if (font.name) {
			style.fontFamily = font.name;
			hasStyle = true;
		}
		const fontColor = extractColor(font.color);
		if (fontColor && fontColor !== '#000000') {
			style.color = fontColor;
			hasStyle = true;
		}
	}

	// Alignment
	const alignment = cell.alignment;
	if (alignment) {
		if (alignment.horizontal && (alignment.horizontal as string) !== 'general') {
			style.align = alignment.horizontal as CellStyle['align'];
			hasStyle = true;
		}
		if (alignment.vertical && alignment.vertical !== 'bottom') {
			style.verticalAlign = alignment.vertical as CellStyle['verticalAlign'];
			hasStyle = true;
		}
		if (alignment.wrapText) {
			style.wrapText = true;
			hasStyle = true;
		}
		if (alignment.indent) {
			style.indent = alignment.indent;
			hasStyle = true;
		}
		if (alignment.textRotation) {
			style.rotation = alignment.textRotation as number;
			hasStyle = true;
		}
	}

	// Borders
	const border = cell.border;
	if (border) {
		const borders: CellStyle['borders'] = {};
		let hasBorder = false;

		const sides: Array<'top' | 'bottom' | 'left' | 'right'> = ['top', 'bottom', 'left', 'right'];
		for (const side of sides) {
			const b = border[side];
			if (b && b.style && (b.style as string) !== 'none') {
				borders[side] = {
					style: b.style,
					color: extractColor(b.color) || '#000000',
				};
				hasBorder = true;
			}
		}

		if (hasBorder) {
			style.borders = borders;
			hasStyle = true;
		}
	}

	// Number format
	if (cell.numFmt && cell.numFmt !== 'General') {
		style.numberFormat = cell.numFmt;
		hasStyle = true;
	}

	return hasStyle ? style : undefined;
}

// Cell Value Extraction

/** Safely extract a display value from an ExcelJS cell */
function getCellDisplayValue(cell: ExcelJS.Cell): string | number | boolean | null {
	const val = cell.value;
	if (val === null || val === undefined) return null;

	if (typeof val === 'string') return val;
	if (typeof val === 'number') return val;
	if (typeof val === 'boolean') return val;

	if (typeof val === 'object') {
		// Rich text
		if ('richText' in val) {
			return (val as ExcelJS.CellRichTextValue).richText.map((rt) => rt.text || '').join('');
		}
		// Hyperlink
		if ('text' in val) {
			return String((val as ExcelJS.CellHyperlinkValue).text || '');
		}
		// Formula
		if ('result' in val) {
			const result = (val as ExcelJS.CellFormulaValue).result;
			if (result instanceof Date) {
				return result.toLocaleDateString();
			}
			return result != null ? String(result) : null;
		}
		// SharedString
		if ('sharedString' in val) {
			return String((val as { sharedString?: string }).sharedString || '');
		}
		// Date
		if (val instanceof Date) {
			return val.toLocaleDateString();
		}
	}

	return String(val);
}

/** Extract formula text if present */
function getCellFormula(cell: ExcelJS.Cell): string | undefined {
	const val = cell.value;
	if (val && typeof val === 'object' && 'formula' in val) {
		return (val as ExcelJS.CellFormulaValue).formula;
	}
	return undefined;
}

/** Extract hyperlink if present */
function getCellHyperlink(cell: ExcelJS.Cell): string | undefined {
	const val = cell.value;
	if (val && typeof val === 'object' && 'hyperlink' in val) {
		return (val as ExcelJS.CellHyperlinkValue).hyperlink;
	}
	// ExcelJS also stores hyperlinks on the cell model
	if ((cell as { hyperlink?: string }).hyperlink) {
		return (cell as { hyperlink?: string }).hyperlink;
	}
	return undefined;
}

// Merge Map Builder

interface MergeInfo {
	range: string;
	master: { col: number; row: number };
	colSpan: number;
	rowSpan: number;
}

/** Build a map of cell address → merge info from the worksheet */
function buildMergeMap(worksheet: ExcelJS.Worksheet): {
	mergeMap: Map<string, MergeInfo>;
	mergedRanges: string[];
} {
	const mergeMap = new Map<string, MergeInfo>();
	const mergedRanges: string[] = [];

	// ExcelJS internal merge storage
	const merges = (
		worksheet as unknown as {
			_merges?: Record<string, { left: number; right: number; top: number; bottom: number }>;
		}
	)._merges;
	if (!merges) return { mergeMap, mergedRanges };

	for (const key of Object.keys(merges)) {
		const range = merges[key];
		if (!range) continue;

		const rangeStr = `${colNumberToLetter(range.left)}${range.top}:${colNumberToLetter(range.right)}${range.bottom}`;
		mergedRanges.push(rangeStr);

		const colSpan = range.right - range.left + 1;
		const rowSpan = range.bottom - range.top + 1;

		for (let r = range.top; r <= range.bottom; r++) {
			for (let c = range.left; c <= range.right; c++) {
				const addr = `${colNumberToLetter(c)}${r}`;
				mergeMap.set(addr, {
					range: rangeStr,
					master: { col: range.left, row: range.top },
					colSpan,
					rowSpan,
				});
			}
		}
	}

	return { mergeMap, mergedRanges };
}

// Main Parser

export async function parseExcelStyled({
	file,
	sheetName,
}: {
	file: File;
	sheetName?: string;
}): Promise<ParseExcelStyledResult> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = async (e) => {
			try {
				const arrayBuffer = e.target?.result as ArrayBuffer;
				const workbook = new ExcelJS.Workbook();
				await workbook.xlsx.load(arrayBuffer);

				// Sheet list
				const sheets: StyledSheetInfo[] = workbook.worksheets.map((ws) => ({
					name: ws.name,
					rowCount: ws.rowCount,
				}));

				// Select sheet
				const targetSheet = sheetName || sheets[0]?.name;
				const worksheet = workbook.getWorksheet(targetSheet);
				if (!worksheet) {
					throw new Error(`Sheet "${targetSheet}" not found`);
				}

				// Build merge map
				const { mergeMap, mergedRanges } = buildMergeMap(worksheet);

				// Determine used range
				const colCount = worksheet.columnCount;
				const rowCount = worksheet.rowCount;

				// Build column order
				const columnOrder: string[] = [];
				const columnWidths: Record<string, number> = {};
				const hiddenColumns: string[] = [];

				for (let c = 1; c <= colCount; c++) {
					const letter = colNumberToLetter(c);
					columnOrder.push(letter);

					const col = worksheet.getColumn(c);
					// ExcelJS width is in "character units" based on the default font.
					// The standard conversion factor is ~8.43 pixels per character unit
					// (varying slightly by font). Enforce a minimum of 40px so columns
					// are never too narrow to read.
					const width = col.width;
					if (width) {
						columnWidths[letter] = Math.max(40, Math.round(width * 8.43));
					}
					if (col.hidden) {
						hiddenColumns.push(letter);
					}
				}

				// Extract rows
				const rows: StyledRow[] = [];
				// Also detect header labels from first data-like row
				const headerLabels: Record<string, string> = {};

				for (let r = 1; r <= rowCount; r++) {
					const wsRow = worksheet.getRow(r);
					const cells: Record<string, StyledCell> = {};
					let hasContent = false;

					for (let c = 1; c <= colCount; c++) {
						const colLetter = colNumberToLetter(c);
						const addr = `${colLetter}${r}`;
						const cell = wsRow.getCell(c);

						const value = getCellDisplayValue(cell);
						const style = extractCellStyle(cell);
						const formula = getCellFormula(cell);
						const hyperlink = getCellHyperlink(cell);
						const mergeInfo = mergeMap.get(addr);

						// Skip non-master merged cells (they'll be spanned)
						if (mergeInfo && (mergeInfo.master.col !== c || mergeInfo.master.row !== r)) {
							continue;
						}

						const styledCell: StyledCell = {
							value,
							col: colLetter,
						};

						if (style) styledCell.style = style;
						if (formula) styledCell.formula = formula;
						if (hyperlink) styledCell.hyperlink = hyperlink;

						if (mergeInfo && mergeInfo.master.col === c && mergeInfo.master.row === r) {
							styledCell.isMergeMaster = true;
							styledCell.mergeRange = mergeInfo.range;
							styledCell.colSpan = mergeInfo.colSpan;
							styledCell.rowSpan = mergeInfo.rowSpan;
						}

						if (value !== null || style || mergeInfo) {
							cells[colLetter] = styledCell;
							hasContent = true;
						}
					}

					// Build header labels from the first row that has content
					if (hasContent && Object.keys(headerLabels).length === 0) {
						for (const [col, cell] of Object.entries(cells)) {
							if (cell.value != null) {
								headerLabels[col] = String(cell.value);
							}
						}
					}

					// Include all rows (even empty ones within the range for proper spacing)
					const styledRow: StyledRow = {
						rowIndex: r - 1,
						cells,
					};

					// Row height
					const h = wsRow.height;
					if (h && h !== 15) {
						// 15 is Excel default
						styledRow.height = Math.round(h * 1.33); // pt to px approx
					}

					if (wsRow.hidden) {
						styledRow.hidden = true;
					}

					if (wsRow.outlineLevel) {
						styledRow.outlineLevel = wsRow.outlineLevel;
					}

					if (hasContent || styledRow.height || styledRow.hidden) {
						rows.push(styledRow);
					}
				}

				// Metadata
				const metadata: SheetMetadata = {
					mergedCells: mergedRanges,
					columnWidths,
					hiddenColumns,
					totalColumns: colCount,
					totalRows: rowCount,
					columnOrder,
					defaultRowHeight: (worksheet.properties as { defaultRowHeight?: number })
						?.defaultRowHeight,
					defaultColWidth: (worksheet.properties as { defaultColWidth?: number })?.defaultColWidth
						? Math.round(
								((worksheet.properties as { defaultColWidth?: number }).defaultColWidth as number) *
									7.5
							)
						: undefined,
					rightToLeft: (worksheet.views?.[0] as { rightToLeft?: boolean })?.rightToLeft,
				};

				// Frozen panes
				if (worksheet.views && worksheet.views.length > 0) {
					const view = worksheet.views[0];
					if (view.state === 'frozen') {
						metadata.frozenRow = view.ySplit;
						metadata.frozenCol = view.xSplit;
					}
				}

				resolve({
					headers: columnOrder,
					headerLabels,
					rows,
					metadata,
					sheets,
					selectedSheet: targetSheet || '',
				});
			} catch (err) {
				reject(err);
			}
		};

		reader.onerror = () => reject(new Error('Failed to read file'));
		reader.readAsArrayBuffer(file);
	});
}
